import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Youtube, Sparkles, Loader2, ClipboardPaste, AlertCircle } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useAnalyses } from "@/context/AnalysisContext";
import { extractVideoId } from "@/lib/utils-app";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  "Fetching YouTube metadata",
  "Reading engagement signals",
  "Scoring 13 creative dimensions",
  "Generating actionable briefs",
];

const SAMPLES = [
  { label: "Apple — Shot on iPhone", url: "https://www.youtube.com/watch?v=xNfxLbf-uHo" },
  { label: "Dollar Shave Club", url: "https://www.youtube.com/watch?v=ZUG9qYTJMsI" },
  { label: "Nike — You Can't Stop Us", url: "https://www.youtube.com/watch?v=WA4dDs0T7sM" },
];

export function AnalyzeVideoModal({ onSuccess }) {
  const { analyzeOpen, closeAnalyze, openPremium } = useUI();
  const { analyze } = useAnalyses();
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!analyzeOpen) {
      setUrl("");
      setError("");
      setBusy(false);
      setStep(0);
    }
  }, [analyzeOpen]);

  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 1400);
    return () => clearInterval(t);
  }, [busy]);

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text);
    } catch {
      toast.error("Clipboard access blocked by your browser.");
    }
  };

  const submit = async () => {
    setError("");
    if (!extractVideoId(url)) {
      setError("That doesn't look like a valid YouTube or Shorts URL.");
      return;
    }
    setBusy(true);
    setStep(0);
    try {
      const analysis = await analyze(url);
      toast.success("Analysis complete");
      closeAnalyze();
      onSuccess?.(analysis);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        closeAnalyze();
        openPremium();
        return;
      }
      setError(formatApiError(e?.response?.data?.detail) || "Analysis failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={analyzeOpen} onOpenChange={(o) => !o && !busy && closeAnalyze()}>
      <DialogContent className="sm:max-w-lg" data-testid="analyze-video-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Youtube className="h-5 w-5" />
            </span>
            <DialogTitle className="font-display text-xl tracking-tight">Analyze YouTube Video</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Paste any public YouTube video or Short. Our AI will break down its hook, pacing,
            audio, audience fit, scroll-stop probability and 9 more creative dimensions.
          </DialogDescription>
        </DialogHeader>

        {!busy ? (
          <div className="space-y-4 pt-2">
            <div className="relative">
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
              <Input
                data-testid="analyze-video-modal-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="Paste a YouTube video or Short URL..."
                className="pl-9 pr-24 h-11"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={paste}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 gap-1 text-xs"
              >
                <ClipboardPaste className="h-3.5 w-3.5" /> Paste
              </Button>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive" data-testid="analyze-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              data-testid="analyze-video-submit-button"
              onClick={submit}
              className="w-full h-11 gap-2 font-medium"
            >
              <Sparkles className="h-4 w-4" /> Analyze Video
            </Button>

            <div>
              <p className="overline text-muted-foreground mb-2">Try a sample</p>
              <div className="flex flex-wrap gap-2">
                {SAMPLES.map((s) => (
                  <button
                    key={s.url}
                    onClick={() => setUrl(s.url)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/60 hover:text-primary transition-colors"
                    data-testid="analyze-sample-btn"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-6 space-y-4" data-testid="analyze-loading">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                {i < step ? (
                  <span className="h-5 w-5 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center text-xs">
                    ✓
                  </span>
                ) : i === step ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : (
                  <span className="h-5 w-5 rounded-full border border-border" />
                )}
                <span className={i <= step ? "text-sm" : "text-sm text-muted-foreground"}>{s}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
